<template>
    <b-modal id="campaign-rule-set-detail-modal"
             :title="modalTitle"
             hide-footer
             size="xl"
             modal-class="default-modal"
             @hide="closeModal"
    >
        <div class="action-btns">
            <template v-if="mode === 'readOnly'">
                <div style="height: 32px"/>
            </template>
            <template v-else-if="mode === 'detail'">
                <b-button variant="outline-primary"
                          @click="changeMode('edit')"
                >
                    {{ $t('수정') }}
                </b-button>
            </template>
            <template v-else-if="mode === 'add'">
                <b-button variant="outline-primary"
                          form="campaign-rule-set-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
            </template>
            <template v-else-if="mode === 'edit'">
                <b-button variant="outline-danger"
                          @click="confirmDelete"
                >
                    {{ $t('삭제') }}
                </b-button>
                <b-button variant="outline-primary"
                          form="campaign-rule-set-form"
                          type="submit"
                >
                    {{ $t('저장') }}
                </b-button>
                <b-button variant="outline-secondary"
                          @click="changeMode('detail')"
                >
                    {{ $t('취소') }}
                </b-button>
            </template>
        </div>
        <!-- 이 규칙셋이 속한 프로젝트로 지금 평가 신청이 가능한지. 목록의 "신청 가능"
             배지와 같은 판정이며, 여기서는 왜 불가능한지(빠진 입력키)까지 보여준다. -->
        <b-alert
            v-if="mode !== 'add' && readiness"
            :variant="readiness.ready ? 'success' : 'danger'"
            show
            class="mb-3"
        >
            <template v-if="readiness.ready">
                {{ $t('이 프로젝트로 평가 신청이 가능합니다.') }}
            </template>
            <template v-else>
                <strong>{{ $t('이 프로젝트로는 현재 평가 신청이 불가능합니다.') }}</strong>
                <div v-if="readiness.reason === 'missing_input_keys'" class="mt-2">
                    {{ $t('규칙이 참조하는 입력키가 이 프로젝트에 등록되지 않았습니다') }}:
                    <b-badge v-for="k in readiness.missing_keys" :key="k" variant="light" class="mr-1">{{ k }}</b-badge>
                    <div class="mt-1 small">
                        {{ $t('[프로젝트 스키마 관리 > 스키마 입력 값 관리] 에서 위 키를 등록한 뒤 다시 시도하세요.') }}
                    </div>
                </div>
                <div v-else-if="readiness.reason === 'no_campaign_mapping'" class="mt-2 small">
                    {{ $t('활성 규칙셋도 없고 프로젝트에 캠페인도 연결되어 있지 않습니다.') }}
                </div>
                <div v-else-if="readiness.reason === 'no_required_keys'" class="mt-2 small">
                    {{ $t('규칙셋에 조건이 하나도 없어 캠페인을 판별할 수 없습니다.') }}
                </div>
            </template>
        </b-alert>
        <div class="tabs-container">
            <b-tabs v-model="tabIndex" class="default-tabs" :class="{'with-actions': mode !== 'add'}" content-class="mt-3">
                <b-tab :title="$t('기본 정보')">
                    <template v-if="loading">
                        <b-skeleton-img no-aspect class="empty-content"></b-skeleton-img>
                    </template>
                    <template v-else>
                        <campaign-rule-set-detail v-if="mode === 'detail' || mode ==='readOnly'"
                                                  :detail-data="detailData"
                                                  :campaigns="campaigns"
                                                  :projects="projects"
                        />
                        <campaign-rule-set-form v-else ref="campaignRuleSetForm" :mode="mode" :detail-data="detailData"
                                                :campaigns="campaigns"
                                                :projects="projects"
                                                @updateDetailData="updateDetailData"
                        />
                    </template>
                </b-tab>
            </b-tabs>
        </div>
    </b-modal>
</template>

<script>
import {deleteCampaignRuleSet, getCampaignRuleSet} from '@shared_utils/api/ruleEngine';
import {getCampaignReadiness} from '@shared_utils/api/projectSchema';

import CampaignRuleSetDetail
    from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/CampaignRuleSetDetail.vue';
import CampaignRuleSetForm
    from '@shared/components/admin/pages/Management/CampaignConnectRule/CampaignRuleSet/CampaignRuleSetForm.vue';

export default {
    name: 'CampaignRuleSetModal',
    components: {
        CampaignRuleSetForm,
        CampaignRuleSetDetail,
    },
    props: {
        detailId: {
            type: [Number, String, null],
            default: null,
        },
        campaigns: {
            type: [Array, null],
            default: null,
        },
        projects: {
            type: [Array, null],
            default: null,
        }
    },
    data() {
        return {
            mode: 'detail', // detail | add | edit | readOnly
            tabIndex: 1,
            detailData: {id: null},
            loading: false,
            // 이 규칙셋 프로젝트의 신청 가능 상태 (campaign-readiness 응답).
            readiness: null,
        };
    },
    computed: {
        modalTitle() {
            if (this.mode === 'add') {
                return this.$t('캠페인 규칙셋 등록');
            } else if (this.mode === 'edit') {
                return this.$t('캠페인 규칙셋 수정');
            } else {
                return this.$t('캠페인 규칙셋 상세');
            }
        },
        readOnly() {
            return this.mode === 'readOnly';
        }
    },
    watch: {
        detailId: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.fetchDetailData();
                } else {
                    this.detailData = {id: null};
                    this.readiness = null;
                }
            }
        }
    },
    methods: {
        changeMode(mode) {
            this.mode = mode;
            this.tabIndex = 1;
        },
        closeModal() {
            if (this.mode === 'edit') {
                this.$refs.campaignRuleSetForm.resetForm();
            }
            this.mode = 'detail';
            this.$bvModal.hide('campaign-rule-set-detail-modal');
        },
        async fetchDetailData() {
            try {
                this.loading = true;
                const res = await getCampaignRuleSet(this.detailId);
                this.detailData = res.data;
                this.fetchReadiness();
            } catch (e) {
                console.error(e);
                console.dir(e);
            } finally {
                this.loading = false;
            }
        },
        async fetchReadiness() {
            this.readiness = null;
            const projectId = this.detailData && this.detailData.project_id;
            if (!projectId) {
                return;
            }
            try {
                const {data} = await getCampaignReadiness(projectId);
                this.readiness = data;
            } catch (e) {
                // 배너만 생략한다. 상세 조회 자체는 이미 성공했다.
            }
        },
        updateDetailData(detailData) {
            if (this.mode === 'add') {
                this.$emit('addData', detailData);
                this.closeModal();
            } else if (this.mode === 'edit') {
                this.$emit('updateDetailData', detailData);
                this.detailData = detailData;
                this.changeMode('detail');
            }
        },
        confirmDelete() {
            this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: this.$t('캠페인 규칙셋 삭제'),
                message: this.$t('현 캠페인 규칙셋을 삭제하시겠습니까?')
            })
                .then((v) => {
                    if (v) {
                        this.deleteCondition();
                    }
                });
        },
        deleteCondition() {
            deleteCampaignRuleSet(this.detailData.id)
                .then(() => {
                    this.$EventBus.$emit('toast-success', this.$t('캠페인 규칙셋 삭제'), this.$t('캠페인 규칙셋이 성공적으로 삭제되었습니다.'));
                    this.$emit('deleteData', this.detailData.id);
                    this.closeModal();
                })
                .catch((error) => {
                    console.error(error);
                    this.$EventBus.$emit('toast-failure', this.$t('캠페인 규칙셋 삭제'), this.$t('캠페인 규칙셋 삭제 중 오류가 발생했습니다. 개발 팀에 문의해주세요.'));
                });
        }
    },
};
</script>

<style scoped lang="scss">

</style>
